import { useMemo, useState } from 'react'
import { useAllUserList } from '../../assets/api/useAdminApi'
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent'
import ZoneHeadListTable from './ZoneHeadListTable'
import AdminToolbar from './components/AdminToolbar'
import CustomDivider from '../../shared/utils/CustomDivider'
import { Field, Formik, Form } from 'formik';
import * as yup from "yup";
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField'
import { Button, Grid2 } from '@mui/material'
import UtItem from '../../shared/utils/UtList'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useAuthStore from '../../store/useAuthStore'
import { Grid_Gap, Input_Width } from '../../shared/constants'
import { useToaster } from '../../shared/utils/ToasterProvider'
import api from '../../assets/nodeApi/axiosInstance'

const ZoneHeadList = () => {
    const { token } = useAuthStore()
    const { data: allUserList, isLoading } = useAllUserList({ role: "ZoneHead" })
    const { data: coordinatorList, isLoading: isLoadingCoordinator } = useAllUserList({ role: "coordinator" })
    const { showToaster } = useToaster();
    const queryClient = useQueryClient();
    const UserOptions = allUserList?.data?.map((item, index) => ({
        label: item?.firstname + " " + item?.lastname,
        value: item?.firstname + " " + item?.lastname,
        id: item?.id,
    })) || []
    const CoordinatorOptions = coordinatorList?.data?.map((item) => ({
        label: item?.firstname + " " + item?.lastname,
        value: item?.firstname + " " + item?.lastname,
        id: item?.id,
    })) || []

    const [selectedCoordinator, setSelectedCoordinator] = useState([])
    const handleRemove = (id) => {
        setSelectedCoordinator(prev => prev.filter(apartment => apartment.id !== id));
    };

    const [searchQuery, setSearchQuery] = useState('');

    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    const originalRow = useMemo(() => {
        return allUserList?.data?.map((item, index) => ({
            sr_no: index + 1,
            ...item
        })) || [];
    }, [allUserList]);

    const filteredRows = useMemo(() => {
        if (!searchQuery.trim()) {
            return originalRow;
        }
        const lowercasedQuery = searchQuery.toLowerCase();
        return originalRow.filter(student =>
            Object.values(student).some(val =>
                val != null && val.toString().toLowerCase().includes(lowercasedQuery)
            )
        );
    }, [originalRow, searchQuery]);

    const mutationAssign = useMutation({
        mutationFn: (formData) => {
            return api.post(`users/assign-reporting`, formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
        },
        onSuccess: (response) => {
            showToaster('Success', response?.data?.message, 'success')
            queryClient.invalidateQueries({ queryKey: ['clusters/by-coordinator'] });
            setSelectedCoordinator([])
        },
        onError: () => {
            showToaster("Error Caught While submiting the form", 'Error', 'error');
        },
    });

    const handleAssign = (values, { setSubmitting, resetForm }) => {
        if (selectedCoordinator.length === 0) {
            showToaster('Add Atleast one Coordinator', "Warning", 'warning');
            return;
        }
        const obj = {
            zone_head_id: values?.user_id,
            user_ids: selectedCoordinator.map(item => item.id)
        }
        mutationAssign.mutate(obj, {
            onSettled: () => {
                setSubmitting(false);
            },
            onSuccess: () => {
                resetForm();
            }
        });
    }
    return (
        <>
            <NormalContainerWithHeader headerText={"Assign Cluster Coordinator to Zone Head"}>
                <Formik
                    initialValues={{ user_id: "" }}
                    validationSchema={yup.object().shape({
                        user_id: yup.string().required("User Id is required"),
                    })}
                    onSubmit={handleAssign}
                >
                    {({ resetForm, setFieldValue }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={12}>
                                    {selectedCoordinator?.map((item, index) => (
                                        <UtItem key={index} text={item?.label} onRemove={() => { handleRemove(item?.id) }} />
                                    ))}
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="user_id"
                                        component={IdAutoCompleteField}
                                        options={UserOptions}
                                        label="Select Zone Head"
                                        isRequired={true}
                                        isLoading={isLoading}

                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="cluster_ids"
                                        component={IdAutoCompleteField}
                                        options={CoordinatorOptions}
                                        label="Select Cluster Coordinator"
                                        isRequired={true}
                                        isLoading={isLoadingCoordinator}
                                        onValueChange={(newValue) => {
                                            setSelectedCoordinator((prev) => {
                                                const exists = prev.find((item) => item.id === newValue.id);
                                                return exists ? prev : [...prev, newValue];
                                            });
                                            setFieldValue("cluster_ids", "")
                                        }}

                                    />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />


                            <Grid2 container spacing={Grid_Gap} sx={{ display: "flex", justifyContent: "end" }}>
                                <Grid2 children size={{ xs: 12, sm: 3 }}>
                                    <Button variant='outlined' fullWidth onClick={() => { resetForm(); setSelectedCoordinator() }}>Cancel</Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 3 }}>
                                    <Button variant='contained' type="submit" fullWidth>Submit</Button>
                                </Grid2>
                            </Grid2>
                            
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>
            <CustomDivider />
            <NormalContainerWithHeader headerText={"Zone Head List"}
                RightContent={
                    <AdminToolbar onSearchChange={handleSearchChange} />
                }
            >
                <ZoneHeadListTable
                    zoneHeadRow={filteredRows}
                    isLoading={isLoading}
                />
            </NormalContainerWithHeader>
        </>
    )
}

export default ZoneHeadList